import type { User } from "firebase/auth";
import { useEffect, useId, useState } from "react";
import * as api from "./api";
import { actorText, batteryPath, changeText, dayTicks, fullShare, gapShare, levelSpans, type Range, RANGES, tickAnchor, tickEvery, withStart, xScale } from "./chart";
import { age, readingTime } from "./format";
import { useMedia } from "./Menu";
import { SERVER_PROBLEM } from "./save";
import { Skeleton } from "./Skeleton";

// A phone draws the chart in fewer units, so the 12 px labels keep their size.
const NARROW = "(max-width: 40rem)";
const HEIGHT = 200;
const LEFT = 40;
const BAND_TOP = 8;
const BAND_BOTTOM = 36;
const LINE_TOP = 52;
const LINE_BOTTOM = HEIGHT - 24;

type Loaded<T> = T | "failed" | undefined;

const problem = (e: unknown) =>
  e instanceof api.ApiError && e.status >= 500 ? SERVER_PROBLEM : "Cannot load this part. Check your connection.";

function ReadingChart({ found, timeZone }: { found: api.TankReadings; timeZone: string }) {
  const narrow = useMedia(NARROW);
  const width = narrow ? 360 : 640;
  const right = width - 8;
  const x = xScale(found.from, found.to, LEFT, right);
  const spans = levelSpans(withStart(found.points, found.before), found.to, x, undefined, found.from);
  const line = batteryPath(found.points, x, LINE_TOP, LINE_BOTTOM);
  const ticks = dayTicks(found.from, found.to, tickEvery(found.days, narrow), x, timeZone);
  const share = Math.round(fullShare(spans) * 100);
  const gap = Math.round(gapShare(spans, right - LEFT) * 100);
  const silent = gap > 0 ? ` No readings for ${gap}% of the time.` : "";
  const summary = `${found.count} readings in ${found.days} days. The container was full for ${share}% of the time with a level.${silent}`;
  return (
    <figure className="tank-chart" data-testid="tank-chart">
      <svg viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={summary}>
        <text x={LEFT - 6} y={(BAND_TOP + BAND_BOTTOM) / 2 + 4} className="chart-axis" textAnchor="end">
          Level
        </text>
        <defs>
          <pattern id="chart-gap" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" className="chart-gap-line" />
          </pattern>
        </defs>
        <rect x={LEFT} y={BAND_TOP} width={right - LEFT} height={BAND_BOTTOM - BAND_TOP} className="chart-band" />
        {spans.map((span) => (
          <rect
            key={span.start}
            x={span.start}
            y={BAND_TOP}
            width={Math.max(1, span.end - span.start)}
            height={BAND_BOTTOM - BAND_TOP}
            className={span.full === null ? "chart-gap" : span.full ? "chart-full" : "chart-not-full"}
            data-level={span.full === null ? "none" : span.full ? "full" : "not-full"}
          />
        ))}
        {[0, 50, 100].map((pct) => {
          const y = LINE_BOTTOM - (pct / 100) * (LINE_BOTTOM - LINE_TOP);
          return (
            <g key={pct}>
              <line x1={LEFT} y1={y} x2={right} y2={y} className="chart-grid" />
              <text x={LEFT - 6} y={y + 4} className="chart-axis" textAnchor="end">
                {pct}%
              </text>
            </g>
          );
        })}
        {line && <path d={line} className="chart-battery" vectorEffect="non-scaling-stroke" />}
        {ticks.map((tick) => (
          <text key={tick.x} x={tick.x} y={HEIGHT - 6} className="chart-axis" textAnchor={tickAnchor(tick.x, LEFT, right)}>
            {tick.label}
          </text>
        ))}
      </svg>
      <figcaption className="chart-key">
        <span className="key-full">Full</span> <span className="key-not-full">Not full</span> <span className="key-gap">No readings</span>{" "}
        <span className="key-battery">Battery %</span> · {summary}
      </figcaption>
    </figure>
  );
}

type Heading = 2 | 4;

function Readings({ id, user, timeZone, heading }: { id: string; user: User; timeZone: string; heading: Heading }) {
  const H = `h${heading}` as const;
  const title = `tank-readings-${useId()}`;
  const [days, setDays] = useState<Range>(30);
  const [found, setFound] = useState<Loaded<api.TankReadings>>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.tankReadings(token, id, days))
      .then((next) => live && setFound(next))
      .catch((e) => {
        if (!live) return;
        setError(problem(e));
        setFound("failed");
      });
    return () => {
      live = false;
    };
  }, [id, user, days, attempt]);
  return (
    <section aria-labelledby={title} data-testid="tank-readings">
      <H id={title}>Readings</H>
      <div className="tabs" role="group" aria-label="Days of readings">
        {RANGES.map((value) => (
          <button key={value} type="button" aria-pressed={days === value} data-testid={`readings-${value}`} onClick={() => setDays(value)}>
            {value} days
          </button>
        ))}
      </div>
      {found === undefined ? (
        <Skeleton shape="chart" />
      ) : found === "failed" ? (
        <p role="alert" className="error">
          {error} <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      ) : found.points.length ? (
        <ReadingChart found={found} timeZone={timeZone} />
      ) : (
        <p data-testid="readings-empty">No readings in the last {found.days} days.</p>
      )}
    </section>
  );
}

function DeviceFacts({ device, now, timeZone }: { device: api.DeviceDetail; now: Date; timeZone: string }) {
  return (
    <dl className="facts" data-testid="tank-device">
      <dt>Device</dt>
      <dd>
        {device.name ?? `${device.source} ${device.external_id}`}
        {device.name && <span className="sub">{`${device.source} ${device.external_id}`}</span>}
      </dd>
      <dt>Last report</dt>
      <dd>{device.last_seen ? `${age(device.last_seen, now)} · ${readingTime(device.last_seen, timeZone)}` : "Never"}</dd>
      <dt>First report</dt>
      <dd>{device.first_seen ? readingTime(device.first_seen, timeZone) : "Never"}</dd>
      <dt>Battery</dt>
      <dd>
        {device.battery_pct === null ? "Unknown" : `${Math.round(device.battery_pct)}%`}
        {device.battery_v !== null && ` · ${device.battery_v.toFixed(2)} V`}
      </dd>
      {device.firmware && (
        <>
          <dt>Firmware</dt>
          <dd>{device.firmware}</dd>
        </>
      )}
    </dl>
  );
}

export function TankDetails({
  id,
  user,
  timeZone,
  now,
  heading = 2,
}: {
  id: string;
  user: User;
  timeZone: string;
  now: Date;
  heading?: Heading;
}) {
  const H = `h${heading}` as const;
  const titles = useId();
  const [details, setDetails] = useState<Loaded<api.TankDetails>>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.tankDetails(token, id))
      .then((next) => live && setDetails(next))
      .catch((e) => {
        if (!live) return;
        setError(problem(e));
        setDetails("failed");
      });
    return () => {
      live = false;
    };
  }, [id, user, attempt]);
  const retry = (
    <p role="alert" className="error">
      {error} <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
    </p>
  );
  return (
    <>
      <Readings id={id} user={user} timeZone={timeZone} heading={heading} />
      <section aria-labelledby={`tank-devices-${titles}`} data-testid="tank-device-panel">
        <H id={`tank-devices-${titles}`}>{details && details !== "failed" && details.devices.length > 1 ? "Devices" : "Device"}</H>
        {details === undefined ? (
          <Skeleton rows={2} />
        ) : details === "failed" ? (
          retry
        ) : details.devices.length ? (
          details.devices.map((device) => <DeviceFacts key={device.id} device={device} now={now} timeZone={timeZone} />)
        ) : (
          <p>No device reports for this container.</p>
        )}
      </section>
      <section aria-labelledby={`tank-history-${titles}`} data-testid="tank-history">
        <H id={`tank-history-${titles}`}>History</H>
        {details === undefined ? (
          <Skeleton rows={3} />
        ) : details === "failed" ? null : details.history.length ? (
          <ol className="history">
            {details.history.map((change, index) => (
              <li key={`${change.at}-${index}`}>
                <span>{changeText(change)}</span>
                <span className="sub">
                  {readingTime(change.at, timeZone)} · {actorText(change.actor)}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p>No changes yet.</p>
        )}
      </section>
    </>
  );
}
